import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/**
 * A custom element that displays complementary products in the cart.
 * Fetches recommendations from all cart items and displays max products.
 *
 * @typedef {Object} CartComplementaryComponentRefs
 * @property {HTMLElement} loadingState - The loading state element.
 * @property {HTMLElement} productsContainer - The products container element.
 * @property {HTMLElement} emptyState - The empty state element.
 */

/**
 * @extends {Component<CartComplementaryComponentRefs>}
 */
class CartComplementaryComponent extends Component {
  requiredRefs = ['loadingState', 'productsContainer', 'emptyState'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Set<string>} */
  #cartProductIds = new Set();

  /** @type {Map<string, Object>} */
  #recommendationsCache = new Map();

  /** @type {number} */
  #loadId = 0;

  /** @type {number} */
  #fetchTimeout = 3000;

  /** @type {number | null} */
  #activeTimeoutId = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    // Delay to ensure DOM is fully ready after morph and other async operations complete
    setTimeout(() => {
      this.#loadRecommendationsFromCart();
    }, 200);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#activeFetch?.abort();
  }

  /**
   * Handles cart update events to refresh recommendations
   * @param {CustomEvent} event
   */
  #handleCartUpdate = async (event) => {
    // If the event comes from cart-items-component, a morph will follow
    // so we should wait for the morph to complete and let connectedCallback handle it
    const source = event.detail?.data?.source;
    if (source === 'cart-items-component') {
      return;
    }

    // For other sources (like cart-complementary adding items), fetch fresh data
    await this.#loadRecommendationsFromCart();
  };

  /**
   * Fetches cart data and loads recommendations based on current cart items
   */
  async #loadRecommendationsFromCart() {
    // Increment load ID to track this specific load operation
    const currentLoadId = ++this.#loadId;

    if (!this.isConnected) return;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await response.json();

      // Check if this load is still the latest one
      if (currentLoadId !== this.#loadId || !this.isConnected) return;

      // Extract product IDs from cart items
      const productIds = cart.items.map((item) => String(item.product_id));

      // Update the data attribute for consistency
      this.dataset.productIds = productIds.join(',');

      await this.#loadRecommendations(productIds, currentLoadId);
    } catch (error) {
      console.error('Error fetching cart:', error);
      // Only hide if this is still the current load
      if (this.isConnected && currentLoadId === this.#loadId) {
        this.hidden = true;
      }
    }
  }

  /**
   * Loads complementary product recommendations from all cart items
   * @param {string[]} [productIdsOverride] - Optional product IDs to use instead of reading from dataset
   * @param {number} [loadId] - The load ID to track this specific load operation
   */
  async #loadRecommendations(productIdsOverride, loadId) {
    // Use provided loadId or get a new one
    const currentLoadId = loadId ?? ++this.#loadId;

    if (!this.isConnected) return;

    const { loadingState, productsContainer, emptyState } = this.refs;
    const maxProducts = parseInt(this.dataset.maxProducts || '3', 10);

    // Use override if provided, otherwise read from dataset
    const productIds = productIdsOverride || (this.dataset.productIds || '').split(',').filter(Boolean);

    // Update cart product IDs set for filtering
    this.#cartProductIds = new Set(productIds);

    // Hide component if cart is empty (only if this is still the current load)
    if (productIds.length === 0) {
      if (currentLoadId === this.#loadId) {
        this.hidden = true;
      }
      return;
    }

    // Keep hidden until we have results - don't show loading state
    productsContainer.innerHTML = '';
    emptyState.classList.add('hidden');
    loadingState.classList.add('hidden');

    // Abort any existing fetch and timeout
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    if (this.#activeTimeoutId) {
      clearTimeout(this.#activeTimeoutId);
      this.#activeTimeoutId = null;
    }

    // Set up timeout to abort fetch and hide component
    let timedOut = false;
    this.#activeTimeoutId = setTimeout(() => {
      timedOut = true;
      this.#activeFetch?.abort();
      loadingState.classList.add('hidden');
      if (currentLoadId === this.#loadId) {
        this.hidden = true;
      }
      this.#activeTimeoutId = null;
    }, this.#fetchTimeout);

    try {
      // Fetch recommendations for all products in parallel
      const recommendationPromises = productIds.map((productId) =>
        this.#fetchRecommendations(productId, this.#activeFetch.signal)
      );

      const results = await Promise.allSettled(recommendationPromises);
      if (this.#activeTimeoutId) {
        clearTimeout(this.#activeTimeoutId);
        this.#activeTimeoutId = null;
      }

      // If we already timed out, don't continue
      if (timedOut) return;

      // Check if this load is still the latest one
      if (currentLoadId !== this.#loadId) return;

      // Check if component is still connected after async operations
      if (!this.isConnected) return;

      // Collect all complementary products
      /** @type {Map<string, Object>} */
      const allProducts = new Map();

      for (const result of results) {
        if (result.status === 'fulfilled' && result.value) {
          for (const product of result.value) {
            // Skip products already in cart
            if (this.#cartProductIds.has(String(product.id))) continue;
            // Use Map to deduplicate by product ID
            if (!allProducts.has(String(product.id))) {
              allProducts.set(String(product.id), product);
            }
          }
        }
      }

      // Convert to array and limit to max products
      const productsToShow = Array.from(allProducts.values()).slice(0, maxProducts);

      loadingState.classList.add('hidden');

      if (productsToShow.length === 0) {
        // Only hide if this is still the current load
        if (currentLoadId === this.#loadId) {
          this.hidden = true;
        }
        return;
      }

      // Only show component when we have products to display
      this.hidden = false;
      this.#renderProducts(productsToShow);
    } catch (error) {
      if (this.#activeTimeoutId) {
        clearTimeout(this.#activeTimeoutId);
        this.#activeTimeoutId = null;
      }
      loadingState.classList.add('hidden');
      // Hide component on any error (including timeout/abort)
      if (currentLoadId === this.#loadId) {
        this.hidden = true;
      }
      if (error.name !== 'AbortError') {
        console.error('Error loading complementary products:', error);
      }
    }
  }

  /**
   * Fetches complementary product recommendations for a single product
   * @param {string} productId
   * @param {AbortSignal} signal
   * @returns {Promise<Object[]>}
   */
  async #fetchRecommendations(productId, signal) {
    // Check cache first
    if (this.#recommendationsCache.has(productId)) {
      return this.#recommendationsCache.get(productId);
    }

    // Only use complementary intent (no related fallback)
    const jsonUrl = `${Theme.routes.product_recommendations_url}.json?product_id=${productId}&limit=10&intent=complementary`;

    try {
      const response = await fetch(jsonUrl, { signal });

      if (response.ok) {
        const data = await response.json();

        if (data.products && data.products.length > 0) {
          this.#recommendationsCache.set(productId, data.products);
          return data.products;
        }
      }
    } catch (e) {
      // Silently fail - no recommendations available
    }

    return [];
  }

  /**
   * Renders the complementary products
   * @param {Object[]} products
   */
  #renderProducts(products) {
    const { productsContainer } = this.refs;

    const html = products
      .map((product) => {
        const hasVariants = product.variants && product.variants.length > 1;
        const firstAvailableVariant =
          product.variants?.find((v) => v.available) || product.variants?.[0];
        const price = this.#formatMoney(firstAvailableVariant?.price || product.price);
        const compareAtPrice = firstAvailableVariant?.compare_at_price || product.compare_at_price;
        const image = product.featured_image || product.images?.[0];

        return `
          <li class="cart-complementary__item" data-product-id="${product.id}">
            <div class="cart-complementary__image-wrapper">
              ${
                image
                  ? `<img
                  class="cart-complementary__image"
                  src="${this.#resizeImage(image, '160x')}"
                  alt="${this.#escapeHtml(product.title)}"
                  loading="lazy"
                  width="80"
                >`
                  : ''
              }
            </div>
            <div class="cart-complementary__item-content">
              <div class="cart-complementary__item-header">
                <div class="cart-complementary__title-row">
                  <h4 class="cart-complementary__item-title">
                    <a href="${product.url}">${this.#escapeHtml(product.title)}</a>
                  </h4>
                  <span class="cart-complementary__item-price">${price}</span>
                </div>
                ${
                  hasVariants && firstAvailableVariant?.title !== 'Default Title'
                    ? `<p class="cart-complementary__item-variant">${this.#escapeHtml(firstAvailableVariant?.title || '')}</p>`
                    : ''
                }
              </div>
              <div class="cart-complementary__item-footer">
                ${
                  hasVariants
                    ? `
                  <div class="cart-complementary__select-wrapper">
                    <select
                      class="cart-complementary__variant-select field__input"
                      data-product-id="${product.id}"
                      aria-label="${Theme.strings?.size || 'Größe'}"
                    >
                      ${product.variants
                        .map(
                          (variant) => `
                        <option
                          value="${variant.id}"
                          data-price="${variant.price}"
                          ${!variant.available ? 'disabled' : ''}
                          ${variant.id === firstAvailableVariant?.id ? 'selected' : ''}
                        >
                          ${this.#escapeHtml(variant.title)}${!variant.available ? ' - Ausverkauft' : ''}
                        </option>
                      `
                        )
                        .join('')}
                    </select>
                    <span class="cart-complementary__select-icon">
                      <svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </span>
                  </div>
                `
                    : ''
                }
                <button
                  type="button"
                  class="cart-complementary__add-button button"
                  data-variant-id="${firstAvailableVariant?.id || product.variants?.[0]?.id}"
                  data-product-id="${product.id}"
                  on:click="/addToCart"
                  ${!firstAvailableVariant?.available ? 'disabled' : ''}
                >
                  ${Theme.strings?.add || 'Hinzufügen'}
                </button>
              </div>
            </div>
          </li>
        `;
      })
      .join('');

    productsContainer.innerHTML = html;

    // Add event listeners for variant selects
    productsContainer.querySelectorAll('.cart-complementary__variant-select').forEach((select) => {
      select.addEventListener('change', this.#handleVariantChange);
    });
  }

  /**
   * Handles variant select change
   * @param {Event} event
   */
  #handleVariantChange = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const selectedOption = select.options[select.selectedIndex];
    const variantId = select.value;
    const item = select.closest('.cart-complementary__item');

    if (!item) return;

    const addButton = item.querySelector('.cart-complementary__add-button');
    const priceElement = item.querySelector('.cart-complementary__item-price');

    if (addButton) {
      addButton.setAttribute('data-variant-id', variantId);
      addButton.disabled = selectedOption.disabled;
    }

    if (priceElement && selectedOption.dataset.price) {
      priceElement.textContent = this.#formatMoney(parseInt(selectedOption.dataset.price, 10));
    }
  };

  /**
   * Adds a product to the cart
   * @param {MouseEvent} event
   */
  addToCart = async (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;

    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.classList.add('cart-complementary__add-button--loading');

    try {
      const sectionId = this.dataset.sectionId;
      const sections = sectionId ? [sectionId] : [];

      // Get all cart-items-component section IDs for updating
      document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
        const id = el.getAttribute('data-section-id');
        if (id && !sections.includes(id)) {
          sections.push(id);
        }
      });

      const config = fetchConfig('json', {
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const response = await fetch(Theme.routes.cart_add_url, config);
      const data = await response.json();

      if (data.status) {
        // Error from Shopify
        console.error('Error adding to cart:', data.description);
        button.disabled = false;
        button.classList.remove('cart-complementary__add-button--loading');
        return;
      }

      // Dispatch cart update event
      document.dispatchEvent(
        new CartAddEvent(data, variantId, {
          source: 'cart-complementary-component',
          itemCount: 1,
          productId: productId,
          sections: data.sections,
        })
      );

      // Morph sections to update cart
      if (data.sections) {
        for (const [sectionId, html] of Object.entries(data.sections)) {
          morphSection(sectionId, /** @type {string} */ (html));
        }
      }

      // Brief success indication
      const originalText = button.textContent;
      button.textContent = Theme.strings?.added || 'Hinzugefügt';
      button.classList.remove('cart-complementary__add-button--loading');

      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1500);
    } catch (error) {
      console.error('Error adding to cart:', error);
      button.disabled = false;
      button.classList.remove('cart-complementary__add-button--loading');
    }
  };

  /**
   * Formats a price in cents to a currency string
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    // Use Shopify's money format or fallback
    const format = Theme.moneyFormat || '{{amount}} EUR';
    return format.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100).toString());
  }

  /**
   * Resizes an image URL to the specified dimensions
   * @param {string} url
   * @param {string} size - e.g. '160x160'
   * @returns {string}
   */
  #resizeImage(url, size) {
    if (!url) return '';
    // Shopify image URL transformation
    return url.replace(/\.(jpg|jpeg|png|gif|webp)/, `_${size}.$1`);
  }

  /**
   * Escapes HTML special characters
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('cart-complementary-component')) {
  customElements.define('cart-complementary-component', CartComplementaryComponent);
}
